<template>
  <Dialog v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot />
    </DialogTrigger>

    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{
          t("pages.organizations.createDialog.title")
        }}</DialogTitle>
        <DialogDescription>
          {{ t("pages.organizations.createDialog.description") }}
        </DialogDescription>
      </DialogHeader>

      <form class="space-y-4" @submit.prevent="onSubmit">
        <div class="space-y-2">
          <Label for="name">{{
            t("pages.organizations.createDialog.nameLabel")
          }}</Label>
          <Input
            id="name"
            v-model="formData.name"
            :placeholder="t('pages.organizations.createDialog.namePlaceholder')"
            :disabled="isLoading"
            required
          />
          <p class="text-sm text-muted-foreground">
            {{ t("pages.organizations.createDialog.nameDescription") }}
          </p>
        </div>

        <DialogFooter class="gap-2">
          <Button
            type="button"
            variant="outline"
            :disabled="isLoading"
            @click="isOpen = false"
          >
            {{ t("pages.organizations.createDialog.cancelButton") }}
          </Button>
          <Button type="submit" :disabled="isLoading || !formData.name.trim()">
            <LoadingIndicator
              v-if="isLoading"
              name="loading"
              class="mr-2"
            />
            <Plus v-else class="w-4 h-4 mr-2" />
            {{ t("pages.organizations.createDialog.createButton") }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { createOrganization, useApiOrganizations } from "~/composables/api";
import { successToast, errorToast } from "~/components/ui/toast";
import { Plus } from "lucide-vue-next";

const emit = defineEmits<{
  created: [organizationId: number];
}>();

const isOpen = ref(false);
const isLoading = ref(false);
const { t } = useI18n();
const { refresh } = await useApiOrganizations();

const formData = reactive({
  name: ""
});

const onSubmit = async () => {
  if (!formData.name.trim()) return;

  isLoading.value = true;

  try {
    const newOrganization = await createOrganization({
      name: formData.name.trim()
    });

    // Refresh the organizations list
    await refresh();

    // Show success toast
    successToast({
      title: t("pages.organizations.createDialog.success.title"),
      description: t("pages.organizations.createDialog.success.description", { name: newOrganization.name }),
    });

    // Reset form and close dialog
    formData.name = "";
    isOpen.value = false;

    // Emit the created event with the new organization ID
    emit("created", newOrganization.id);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Failed to create organization:", error);
    
    // Show error toast
    errorToast({
      title: t("pages.organizations.createDialog.error.title"),
      description: error?.data?.message || t("pages.organizations.createDialog.error.description"),
    });
  } finally {
    isLoading.value = false;
  }
};
</script>
